import type { Ref } from "vue";
import { watchEffect } from "vue";
import { useProfile, useWorkspace } from "@/features/workspace/api/use-workspace";
import { setTenantDisplay } from "@/shared/format";
import { resolvePanelLocale, setPanelLocale } from "@/shared/i18n";

/**
 * THE SHELL DECIDING WHAT LANGUAGE THE PANEL IS IN — the only place that decides.
 *
 * ── WHY IT LIVES IN `app/` ────────────────────────────────────────────────
 *
 * The cascade needs two facts that belong to two different owners: the PERSON's
 * preference, from the profile, and the WORKSPACE's language, from the account.
 * A feature cannot read another feature and `shared/` cannot read either, so the
 * shell — the one layer allowed to see both — is where they meet. What `shared/`
 * owns is the DECISION (`resolvePanelLocale`) and the ACT (`setPanelLocale`);
 * this file only carries the facts to them.
 *
 * ── AND WHY IT IS AN EFFECT AND NOT A ONE-SHOT AT BOOT ────────────────────
 *
 * Both facts arrive late and can change while the app is open: the profile
 * answers after the session is restored, someone changes the workspace language
 * on the settings screen, and signing in as a different person changes the
 * answer entirely. Re-running on every change is what makes all three land
 * without a reload, and re-resolving is cheap — `setPanelLocale` writing the same
 * locale twice is a no-op the eye never sees.
 */
export function usePanelLocale(signedIn: Ref<boolean>): void {
	const { data: workspace } = useWorkspace(() => signedIn.value);
	// Not while signed out: the public form, the public chat and the sign-in page
	// mount this same shell, and asking a logged-out browser who it is only
	// produces a 401 in the console.
	const { data: profile } = useProfile(() => signedIn.value);

	/**
	 * THE CASCADE: this person → this workspace → this browser → English.
	 *
	 * The browser is asked last and the workspace outranks it deliberately: a team
	 * issued laptops in English still works in Portuguese, and `tenant.language` is
	 * the only one of the three somebody actually chose.
	 */
	watchEffect(() => {
		setPanelLocale(
			resolvePanelLocale({
				user: profile.value?.language,
				workspace: workspace.value?.language,
				browser: typeof navigator === "undefined" ? [] : navigator.languages,
			}),
		);
	});

	/**
	 * What the WORKSPACE is, for everything that formats a number.
	 *
	 * Currency and timezone are facts about the business and never about the
	 * reader — a colleague working from Lisbon must see the same money and the
	 * same hours the owner sees. The locale published here is the workspace's too,
	 * because this is the context a CUSTOMER-facing surface reads; the operator's
	 * own panel formatting takes the panel language from `panelContext()`.
	 */
	watchEffect(() => {
		setTenantDisplay({
			locale: workspace.value?.language ?? "",
			currency: workspace.value?.currency ?? "",
			timeZone: workspace.value?.timezone ?? "",
			// O país junto com os outros três: é ele que diz qual bandeira e qual DDI
			// um campo de telefone abre, e a coluna já vem nesta mesma resposta.
			country: workspace.value?.country,
		});
	});
}
